import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ALLOWED_ATTACHMENT_CONTENT_TYPES, DEFAULT_ATTACHMENT_MAX_FILE_SIZE_MB } from '@mmf/contracts';
import { uploadAttachment } from '../api/client';

interface AttachmentUploadProps {
  kind: 'topic' | 'comment';
  id: string;
  onUploaded: () => void | Promise<void>;
}

/**
 * Only ever rendered for the topic's/comment's own author — same authorship
 * gate Edit/Delete already use, matching what the API itself enforces
 * (AttachmentsService.upload's requireOwnTarget).
 */
export function AttachmentUpload({ kind, id, onUploaded }: AttachmentUploadProps): React.JSX.Element {
  const [file, setFile] = useState<File | null>(null);
  const [piiAcknowledged, setPiiAcknowledged] = useState(false);
  const fieldId = `attachment-file-${kind}-${id}`;
  const checkboxId = `attachment-pii-${kind}-${id}`;

  const mutation = useMutation({
    mutationFn: () => {
      if (!file) {
        throw new Error('No file selected');
      }
      return uploadAttachment(kind, id, file, piiAcknowledged);
    },
    onSuccess: async () => {
      setFile(null);
      setPiiAcknowledged(false);
      await onUploaded();
    },
  });

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    mutation.mutate();
  };

  return (
    <form onSubmit={handleSubmit} className="attachment-upload">
      <label htmlFor={fieldId}>Attach a file</label>
      <input
        id={fieldId}
        type="file"
        accept={ALLOWED_ATTACHMENT_CONTENT_TYPES.join(',')}
        onChange={(event) => setFile(event.target.files?.[0] ?? null)}
      />
      <p className="hint">
        Images, screenshots, or PDFs, up to {DEFAULT_ATTACHMENT_MAX_FILE_SIZE_MB}MB.
      </p>

      <span className="checkbox">
        <input
          type="checkbox"
          id={checkboxId}
          checked={piiAcknowledged}
          onChange={(event) => setPiiAcknowledged(event.target.checked)}
        />
        <label htmlFor={checkboxId}>I confirm this file contains no client personal information</label>
      </span>

      {mutation.error && (
        <p role="alert" className="notice warning" data-testid={`attachment-upload-error-${kind}-${id}`}>
          Could not upload that file. Try again.
        </p>
      )}

      <button
        type="submit"
        className="button small secondary"
        disabled={!file || !piiAcknowledged || mutation.isPending}
        data-testid={`attachment-upload-submit-${kind}-${id}`}
      >
        {mutation.isPending ? 'Uploading…' : 'Upload'}
      </button>
    </form>
  );
}
